import type { Ring, Vec2 } from '../../geo/types';
import type { Obstacle } from '../types';
import { obbCorners } from '../../geo/polygon';
import { bufferPolyline } from '../../geo/clip';
import { BLOCKHOUSE, type PlacedBlockhouse } from './blockhouse';
import { BOOM_HOUSE, type PlacedBoomHouse } from './boomHouse';
import { PILE_RADIUS, closedRows, type PalisadeData, type PalisadeSpan } from './palisade';
import type { PlacedShip } from './shipPlacement';

const ROUND_SIDES = 16;

export interface HarbourStanding {
  readonly ships: readonly PlacedShip[];
  readonly palisades: readonly PalisadeData[];
  readonly houses: readonly PlacedBoomHouse[];
  readonly blockhouses: readonly PlacedBlockhouse[];
}

function square(centre: Vec2, half: number, turn: number): Ring {
  const [c, s] = [Math.cos(turn), Math.sin(turn)];
  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([i, k]): Vec2 => {
    const [lx, lz] = [i! * half, k! * half];
    return [centre[0] + lx * c + lz * s, centre[1] + lx * s - lz * c];
  });
}

function round(centre: Vec2, radius: number): Ring {
  return Array.from({ length: ROUND_SIDES }, (_, k): Vec2 => {
    const a = (2 * Math.PI * k) / ROUND_SIDES;
    return [centre[0] + Math.cos(a) * radius, centre[1] + Math.sin(a) * radius];
  });
}

function rows({ palisades, houses, blockhouses }: HarbourStanding): readonly Obstacle[] {
  return palisades.flatMap((palisade) => {
    const openings: readonly PalisadeSpan[] = [...houses, ...blockhouses]
      .filter((standing) => standing.palisadeId === palisade.id)
      .map((standing) => standing.opening);
    return closedRows(palisade, openings)
      .filter((row) => row.length >= 2)
      .flatMap((row) => bufferPolyline(row, PILE_RADIUS, 'butt'))
      .map((strip, index) => ({
        id: `${palisade.id}-row-${index}`,
        kind: 'structure' as const,
        polygon: strip.outer,
      }));
  });
}

/** What stands in the harbour water where a rowing boat cannot go: every hull with its rig, the
 *  closed stretches of the pile rows, and the houses standing on them. The rows stay open where
 *  the boom houses leave them open, as they are drawn. */
export function harbourBarriers(standing: HarbourStanding): readonly Obstacle[] {
  return [
    ...standing.ships.map((ship) => ({
      id: ship.id,
      kind: 'vessel' as const,
      polygon: [...obbCorners(ship.footprint)],
    })),
    ...rows(standing),
    ...standing.houses.map((house) => ({
      id: house.id,
      kind: 'structure' as const,
      polygon: square(house.position, BOOM_HOUSE.plan / 2, house.turn),
    })),
    ...standing.blockhouses.map((blockhouse) => ({
      id: blockhouse.id,
      kind: 'structure' as const,
      polygon: round(blockhouse.position, BLOCKHOUSE.radius),
    })),
  ];
}
